<template>
  <div>
    <TitleBar>
      <template v-slot:title><translate>Revenue and Expenses</translate></template>
      <template v-slot:buttons>
        <GekkoButton :text="$gettext('All reports')" :to="{ name: 'Reports' }" icon="fa fa-chart-column" />
      </template>
    </TitleBar>

    <div class="content">
      <FilterBar
        cookieName="RevenueExpensesFilterOptionsVue"
        :mainFilters="['RevenueExpensesDateRangeSelector']"
        filterBarType="revenue-expenses"
        @change="filtersChanged"
      >
      </FilterBar>
      <div class="py-4">
        <CashflowReportChart :chartData="chartData" :loading="loading" />
      </div>
      <RevenueExpensesTable :revenueData="revenueData" :year="selectedYear.toString()" :yearData="yearData" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import TitleBar from '@/components/TitleBar'
import FilterBar from '@/components/list_filters/FilterBar'
import RevenueExpensesTable from '@/components/cash_flow/RevenueExpensesTable'
import CashflowReportChart from '@/components/cash_flow/CashflowReportChart'
import GekkoButton from '@/components/GekkoButton'
import CostService from '@/store/services/cost.service'
import InvoiceService from '@/store/services/invoice.service'

export default {
  name: 'CashFlow',
  components: { TitleBar, FilterBar, GekkoButton, RevenueExpensesTable, CashflowReportChart },
  data() {
    return {
      groupBy: 'quarter',
      selectedMonth: 0,
      selectedYear: new Date().getFullYear(),
      revenueData: [],
      yearData: {},
      chartData: null,
      loading: null,
    }
  },
  computed: {
    ...mapGetters({
      getCompanyId: 'main/getCompanyId',
    }),
  },
  created() {
    this.getData()
  },
  methods: {
    getData() {
      this.chartData = null
      this.loading = true

      var startEndDateInterval = this._interval(this.selectedYear)
      var start_date = startEndDateInterval.start_date, end_date = startEndDateInterval.end_date

      var paidIncomePromise = InvoiceService.totalNet({
        start_date: start_date,
        end_date: end_date,
        sender_company: this.getCompanyId,
        status: 'p',
        group_by: this.groupBy,
      })
      var unpaidIncomePromise = InvoiceService.totalNet({
        start_date: start_date,
        end_date: end_date,
        sender_company: this.getCompanyId,
        status: 's',
        group_by: this.groupBy,
      })
      var expensesPromise = CostService.totalNet({
        owner_company: this.getCompanyId,
        start_date: start_date,
        end_date: end_date,
        group_by: this.groupBy,
      })

      var unpaidExpensesPromise = CostService.totalNet({
        owner_company: this.getCompanyId,
        start_date: start_date,
        end_date: end_date,
        payment_type: 'notpaid',
        group_by: this.groupBy,
      })

      return Promise.all([paidIncomePromise, unpaidIncomePromise, expensesPromise, unpaidExpensesPromise]).then(
        (response) => {
          var responsePaidIncome = response[0].data,
            responseUnpaidIncome = response[1].data,
            responseExpenses = response[2].data,
            responseUnpaidExpenses = response[3].data
          this.revenueData = []

          this.yearData = {}
          this.yearData.label = this.selectedYear
          this.yearData.expenses = 0
          this.yearData.unpaidexpenses = 0
          this.yearData.income = 0
          this.yearData.unpaidincome = 0
          this.yearData.margin = 0

          for (var i = 0; i < responseExpenses.length; i++) {
            var label,
              totalIncomePerGroup = responsePaidIncome[i] + responseUnpaidIncome[i],
              incomePerGroup = Math.round(responsePaidIncome[i]),
              unpaidIncomePerGroup = Math.round(responseUnpaidIncome[i]),
              totalExpensesPerGroup = responseExpenses[i],
              expensesPerGroup = responseExpenses[i] - responseUnpaidExpenses[i],
              unpaidExpensesPerGroup = responseUnpaidExpenses[i],
              margin = Math.round(totalIncomePerGroup - responseExpenses[i])
            if (responseExpenses.length == 4) {
              label = 'Q' + (i + 1)
            } else {
              label = Date.today().moveToMonth(i).toString('MMM')
            }

            this.revenueData.push({
              expenses: expensesPerGroup,
              unpaidexpenses: unpaidExpensesPerGroup,
              income: incomePerGroup,
              unpaidincome: unpaidIncomePerGroup,
              label: label,
              margin: margin,
            })

            this.yearData.expenses += expensesPerGroup
            this.yearData.unpaidexpenses += unpaidExpensesPerGroup
            this.yearData.income += incomePerGroup
            this.yearData.unpaidincome += unpaidIncomePerGroup
            this.yearData.margin += margin
          }

          this._convertToChartData()
          this.loading = false

          return response
        }
      )
    },
    _convertToChartData() {
      this.chartData = {
        labels: this.revenueData.map((x) => x.label),

        datasets: [
          {
            type: 'line',
            label: this.$gettext('Margin:'),
            data: this.revenueData.map((x) => parseInt(x.margin)),
            borderColor: '#37A0CD',
          },
          {
            type: 'bar',
            label: this.$gettext('Unpaid invoices:'),
            data: this.revenueData.map((x) => parseInt(x.unpaidincome)),
            backgroundColor: '#22ac8A',
            stack: 'a',
          },
          {
            type: 'bar',
            label: this.$gettext('Paid invoices:'),
            data: this.revenueData.map((x) => parseInt(x.income)),
            backgroundColor: '#22ac8A',
            stack: 'b',
          },
          {
            type: 'bar',
            label: this.$gettext('Expenses:'),
            data: this.revenueData.map((x) => parseInt(x.expenses)),
            backgroundColor: '#ac8255',
            stack: 'c',
          },
        ],
      }
    },
    _interval(selectedOption) {
      var interval = {}
      var year = parseInt(selectedOption)
      interval.start_date = new Date(year, 0, 1).toString('yyyy-MM-dd')
      interval.end_date = new Date(year, 11, 31).toString('yyyy-MM-dd')
      return interval
    },
    filtersChanged(filters) {
      if (filters.revenue_expenses_options) {
        this.groupBy = filters.revenue_expenses_options.groupBy
        this.selectedYear = filters.revenue_expenses_options.year
      } else {
        this.groupBy = 'quarter'
        this.selectedYear = new Date().getFullYear()
      }
      this.getData()
    },
  },
}
</script>

<style scoped>
</style>